import { useState, useCallback } from 'react';
import { Modal } from '@/components';
import { Phone, Delete } from 'lucide-react';
import '@/assets/styles/components/phone-dialer-modal.styles.scss';

interface PhoneDialerModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCall: (phoneNumber: string) => void;
}

const NUMPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

export const PhoneDialerModal = ({ isOpen, onClose, onCall }: PhoneDialerModalProps) => {
	const [phoneNumber, setPhoneNumber] = useState('');

	const handleKeyPress = useCallback((key: string) => {
		setPhoneNumber(prev => {
			if (prev.length >= 15) return prev;
			return prev + key;
		});
	}, []);

	const handleBackspace = useCallback(() => {
		setPhoneNumber(prev => prev.slice(0, -1));
	}, []);

	const handleCall = useCallback(() => {
		if (phoneNumber.length < 10) return;
		onCall(phoneNumber);
		setPhoneNumber('');
		onClose();
	}, [phoneNumber, onCall, onClose]);

	const handleClose = useCallback(() => {
		setPhoneNumber('');
		onClose();
	}, [onClose]);

	const formatPhoneDisplay = (number: string): string => {
		if (!number) return '';
		const cleaned = number.replace(/[^0-9*#]/g, '');
		if (cleaned.length <= 4) return cleaned;
		if (cleaned.length <= 7) return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
		return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			size="sm"
			variant="default"
			className="phone-dialer-modal"
			hasHeader={true}
			title="Telefon"
			showCloseButton={true}
			closeOnClickOutside={true}>
			<div className="phone-dialer">
				<div className="phone-dialer__input-container">
					<input
						type="tel"
						className="phone-dialer__input"
						value={formatPhoneDisplay(phoneNumber)}
						placeholder="Telefon numarası girin"
						onChange={e => {
							const raw = e.target.value.replace(/[^0-9*#]/g, '');
							if (raw.length <= 15) setPhoneNumber(raw);
						}}
						autoFocus
					/>
					{phoneNumber.length > 0 && (
						<button
							className="phone-dialer__backspace"
							onClick={handleBackspace}
							aria-label="Sil">
							<Delete size={20} />
						</button>
					)}
				</div>

				<div className="phone-dialer__numpad">
					{NUMPAD_KEYS.map(key => (
						<button
							key={key}
							className="phone-dialer__key"
							onClick={() => handleKeyPress(key)}>
							<span className="phone-dialer__key-number">{key}</span>
						</button>
					))}
				</div>

				<button
					className={`phone-dialer__call-btn ${phoneNumber.length >= 10 ? '' : 'disabled'}`}
					onClick={handleCall}
					disabled={phoneNumber.length < 10}>
					<Phone size={20} />
					<span>Ara</span>
				</button>
			</div>
		</Modal>
	);
};
